import {
    InputControl,
    Row,
    Col,
    Container,
    RadioControl,
    FieldsetControl,
} from '@si-armes/dsfr-react';
import React, { useEffect, useId } from 'react';
import InputFiles from 'components/Commun/Form/InputFiles';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { getPatternHookFormValidation } from '../../../../../../../utils/utils';
import { REGEXP_NUMERO_GUICHET_UNIQUE } from '../../../../../../../constants/contraintes.constants';
import {
    FEATURE_APPEL_API_FFTIR,
    isFeatureActive,
} from '../../../../../../../components/FeatureFlipping/featureFlipping.func';
import styles from './InformationLicencieMineur.module.scss';

const InformationLicencieMineur = ({ featureFlipping }) => {
    const {
        register,
        watch,
        formState,
        unregister,
        setError,
        clearErrors,
        trigger,
        getValues,
        setValue,
    } = useFormContext();
    const { message } = useSelector((state) => state.compte.creer.renseignerIdentite);

    const formaterChampsNumeroGuichetUnique = (value) => {
        return value.replace(/\D/g, '');
    };

    const selectedType = watch('typeLicenceMineur');

    const hasValuePermisChasse = () => {
        const numero = getValues('numeroPermisChasseur');
        const fichier = getValues('permisDeChasse');
        return (numero && numero.length > 0) || (fichier && fichier.length > 0);
    };

    const hasValueTir = () => {
        const numero = getValues('numeroLicenceTir');
        const select = watch('selectLicencieOrAncienLicencieTir');
        return (numero && numero.length > 0) || select === 'isAncienLicencie';
    };

    const hasValueBallTrap = () => {
        const numero = getValues('numeroLicenceBallTrap');
        const select = watch('selectLicencieOrAncienLicencieBallTrap');
        return (numero && numero.length > 0) || select === 'isAncienLicencie';
    };

    const hasValueSki = () => {
        const numero = getValues('numeroLicenceSki');
        const select = watch('selectLicencieOrAncienLicencieSki');
        return (numero && numero.length > 0) || select === 'isAncienLicencie';
    };

    useEffect(() => {
        if (watch('selectLicencieOrAncienLicencieTir') !== 'isLicencie') {
            unregister('versoLicenceTir');
        }
    }, [watch('selectLicencieOrAncienLicencieTir')]);

    useEffect(() => {
        if (watch('selectLicencieOrAncienLicencieBallTrap') !== 'isLicencie') {
            unregister('versoLicenceBallTrap');
        }
    }, [watch('selectLicencieOrAncienLicencieBallTrap')]);

    useEffect(() => {
        if (watch('selectLicencieOrAncienLicencieSki') !== 'isLicencie') {
            unregister('versoLicenceSki');
        }
    }, [watch('selectLicencieOrAncienLicencieSki')]);

    useEffect(() => {
        if (!watch('numeroPermisChasseur')) {
            setValue('permisDeChasse', null);
            setValue('numeroGuichetUnique', '');
            setValue('validationPermisChasser', null);
        }
    }, [watch('numeroPermisChasseur')]);

    useEffect(() => {
        trigger(['validationPermisChasser']);
    }, [watch('numeroGuichetUnique')]);

    useEffect(() => {
        const messagesErreur = ['Le numéro de Licence', 'Ce numéro de licence'];
        if (message && messagesErreur.some((val) => message.includes(val))) {
            setError('numeroLicenceSki');
        }
    }, [message]);

    const viderAutreChamps = (activeType) => {
        if (activeType !== 'permisChasse') {
            setValue('numeroPermisChasseur', '');
            setValue('permisDeChasse', null);
            setValue('numeroGuichetUnique', '');
            setValue('validationPermisChasser', null);
        }
        if (activeType !== 'tir') {
            setValue('numeroLicenceTir', '');
            setValue('versoLicenceTir', null);
            setValue('selectLicencieOrAncienLicencieTir', '');
        }
        if (activeType !== 'ballTrap') {
            setValue('numeroLicenceBallTrap', '');
            setValue('versoLicenceBallTrap', null);
            setValue('selectLicencieOrAncienLicencieBallTrap', '');
        }
        if (activeType !== 'ski') {
            setValue('numeroLicenceSki', '');
            setValue('versoLicenceSki', null);
            setValue('selectLicencieOrAncienLicencieSki', '');
        }
    };

    useEffect(() => {
        const hasPermisChasse = hasValuePermisChasse();
        const hasTir = hasValueTir();
        const hasBallTrap = hasValueBallTrap();
        const hasSki = hasValueSki();

        const currentType = selectedType;

        // Si un type est déjà sélectionné et qu'on le vide, on reset tout
        if (currentType) {
            const currentIsEmpty =
                (currentType === 'permisChasse' && !hasPermisChasse) ||
                (currentType === 'tir' && !hasTir) ||
                (currentType === 'ballTrap' && !hasBallTrap) ||
                (currentType === 'ski' && !hasSki);

            if (currentIsEmpty) {
                setValue('typeLicenceMineur', '');
                setValue('numeroPermisChasseur', '');
                setValue('permisDeChasse', null);
                setValue('numeroGuichetUnique', '');
                setValue('validationPermisChasser', null);
                setValue('numeroLicenceTir', '');
                setValue('versoLicenceTir', null);
                setValue('selectLicencieOrAncienLicencieTir', '');
                setValue('numeroLicenceBallTrap', '');
                setValue('versoLicenceBallTrap', null);
                setValue('selectLicencieOrAncienLicencieBallTrap', '');
                setValue('numeroLicenceSki', '');
                setValue('versoLicenceSki', null);
                setValue('selectLicencieOrAncienLicencieSki', '');
                setValue('dateNaissance', '');
            }
            return;
        }

        // Si aucun type n'est sélectionné, on active celui qui a des valeurs
        let newType = null;
        if (hasPermisChasse) {
            newType = 'permisChasse';
        } else if (hasTir) {
            newType = 'tir';
        } else if (hasBallTrap) {
            newType = 'ballTrap';
        } else if (hasSki) {
            newType = 'ski';
        }

        if (newType && newType !== currentType) {
            setValue('typeLicenceMineur', newType);
            setValue('dateNaissance', '');
            viderAutreChamps(newType);
        }
    }, [
        watch('numeroPermisChasseur'),
        watch('permisDeChasse'),
        watch('numeroGuichetUnique'),
        watch('validationPermisChasser'),
        watch('numeroLicenceTir'),
        watch('selectLicencieOrAncienLicencieTir'),
        watch('numeroLicenceBallTrap'),
        watch('selectLicencieOrAncienLicencieBallTrap'),
        watch('numeroLicenceSki'),
        watch('selectLicencieOrAncienLicencieSki'),
    ]);

    return (
        <Container fluid={false} style={{ paddingLeft: 0, paddingRight: 0 }}>
            <input type="hidden" {...register('typeLicenceMineur')} />
            <Row gutters className={styles.rowMotifs}>
                <Col col={12} lg={3}>
                    <div className={styles.motifCard}>
                        <span className={styles.cardLabel}>Chasseur</span>
                    </div>
                    <div className={styles.formSection}>
                        <InputControl
                            id="numeroPermisChasseurMineur"
                            name="numeroPermisChasseur"
                            label="Numéro de permis de chasser *"
                            hintText="Veuillez vous rapprocher de la fédération départementale de votre lieu actuel de résidence si vous ne disposez pas d'un numéro de permis de chasser"
                            {...register('numeroPermisChasseur')}
                            uppercase
                            required
                            disabled={selectedType && selectedType !== 'permisChasse'}
                        />
                        <InputFiles
                            name="permisDeChasse"
                            label="Ajouter mon permis de chasser"
                            error={formState.errors ? formState.errors.permisDeChasse : null}
                            required
                            disabled={selectedType && selectedType !== 'permisChasse'}
                        />
                        <InputControl
                            id="numeroGuichetUniqueMineur"
                            name="numeroGuichetUnique"
                            label="Numéro de guichet unique de votre validation en cours"
                            {...register('numeroGuichetUnique', {
                                pattern: getPatternHookFormValidation(
                                    REGEXP_NUMERO_GUICHET_UNIQUE,
                                    'numero guichet unique',
                                    'Veuillez renseigner a minima 14 caractères',
                                ),
                            })}
                            state={formState.errors?.numeroGuichetUnique ? 'error' : null}
                            message={formState.errors?.numeroGuichetUnique?.message}
                            maskFonction={formaterChampsNumeroGuichetUnique}
                            minLength="14"
                            hintText="Ce champ n'est pas obligatoire. Le numéro de guichet unique de la validation de permis de chasser de la fédération nationale des chasseurs est constitué de 14 chiffres."
                            disabled={selectedType && selectedType !== 'permisChasse'}
                        />
                        <InputFiles
                            label="Ajouter ma validation de permis de chasser"
                            id="validationPermisChasserMineur"
                            name="validationPermisChasser"
                            error={
                                formState.errors ? formState.errors.validationPermisChasser : null
                            }
                            required={
                                selectedType === 'permisChasse' && getValues('numeroGuichetUnique')
                            }
                            disabled={
                                (selectedType && selectedType !== 'permisChasse') ||
                                !getValues('numeroGuichetUnique')
                            }
                        />
                    </div>
                </Col>
                <Col col={12} lg={3}>
                    <div className={styles.motifCard}>
                        <span className={styles.cardLabel}>Tireur sportif</span>
                    </div>
                    <div className={styles.formSection}>
                        <FieldsetControl
                            id="selectLicencieOrAncienLicencieTirMineur"
                            state={
                                formState.errors?.selectLicencieOrAncienLicencieTir ? 'error' : null
                            }
                            message={formState.errors?.selectLicencieOrAncienLicencieTir?.message}
                        >
                            <RadioControl
                                id={useId()}
                                label="Licencié"
                                value="isLicencie"
                                {...register('selectLicencieOrAncienLicencieTir')}
                                disabled={selectedType && selectedType !== 'tir'}
                            />
                            <RadioControl
                                id={useId()}
                                label="Ancien licencié"
                                value="isAncienLicencie"
                                {...register('selectLicencieOrAncienLicencieTir')}
                                disabled={selectedType && selectedType !== 'tir'}
                            />
                        </FieldsetControl>
                        <InputControl
                            id="numeroLicenceTirMineur"
                            name="numeroLicenceTir"
                            label={`Numéro de licence de Tir ${
                                watch('selectLicencieOrAncienLicencieTir') === 'isLicencie'
                                    ? '*'
                                    : ''
                            }`}
                            disabled={
                                !watch('selectLicencieOrAncienLicencieTir') ||
                                (selectedType && selectedType !== 'tir')
                            }
                            {...register('numeroLicenceTir')}
                            uppercase
                        />
                        {!isFeatureActive(featureFlipping, FEATURE_APPEL_API_FFTIR) &&
                            watch('selectLicencieOrAncienLicencieTir') && (
                                <InputFiles
                                    name="versoLicenceTir"
                                    label="Importer la licence"
                                    error={
                                        formState.errors ? formState.errors.versoLicenceTir : null
                                    }
                                    required={
                                        watch('selectLicencieOrAncienLicencieTir') === 'isLicencie'
                                    }
                                    disabled={selectedType && selectedType !== 'tir'}
                                />
                            )}
                    </div>
                </Col>
                <Col col={12} lg={3}>
                    <div className={styles.motifCard}>
                        <span className={styles.cardLabel}>Ball-Trap</span>
                    </div>
                    <div className={styles.formSection}>
                        <FieldsetControl
                            id="selectLicencieOrAncienLicencieBallTrapMineur"
                            state={
                                formState.errors?.selectLicencieOrAncienLicencieBallTrap
                                    ? 'error'
                                    : null
                            }
                            message={
                                formState.errors?.selectLicencieOrAncienLicencieBallTrap?.message
                            }
                        >
                            <RadioControl
                                id={useId()}
                                label="Licencié"
                                value="isLicencie"
                                {...register('selectLicencieOrAncienLicencieBallTrap')}
                                disabled={selectedType && selectedType !== 'ballTrap'}
                            />
                            <RadioControl
                                id={useId()}
                                label="Ancien licencié"
                                value="isAncienLicencie"
                                {...register('selectLicencieOrAncienLicencieBallTrap')}
                                disabled={selectedType && selectedType !== 'ballTrap'}
                            />
                        </FieldsetControl>
                        <InputControl
                            id="numeroLicenceBallTrapMineur"
                            name="numeroLicenceBallTrap"
                            label={`Numéro de licence de Ball-Trap ${
                                watch('selectLicencieOrAncienLicencieBallTrap') === 'isLicencie'
                                    ? '*'
                                    : ''
                            }`}
                            disabled={
                                !watch('selectLicencieOrAncienLicencieBallTrap') ||
                                (selectedType && selectedType !== 'ballTrap')
                            }
                            {...register('numeroLicenceBallTrap')}
                            uppercase
                        />
                        {watch('selectLicencieOrAncienLicencieBallTrap') && (
                            <InputFiles
                                name="versoLicenceBallTrap"
                                label="Importer la licence"
                                error={
                                    formState.errors ? formState.errors.versoLicenceBallTrap : null
                                }
                                required={
                                    watch('selectLicencieOrAncienLicencieBallTrap') === 'isLicencie'
                                }
                                disabled={selectedType && selectedType !== 'ballTrap'}
                            />
                        )}
                    </div>
                </Col>
                <Col col={12} lg={3}>
                    <div className={styles.motifCard}>
                        <span className={styles.cardLabel}>Biathlète</span>
                    </div>
                    <div className={styles.formSection}>
                        <FieldsetControl
                            id="selectLicencieOrAncienLicencieSkiMineur"
                            state={
                                formState.errors?.selectLicencieOrAncienLicencieSki ? 'error' : null
                            }
                            message={formState.errors?.selectLicencieOrAncienLicencieSki?.message}
                        >
                            <RadioControl
                                id={useId()}
                                label="Licencié"
                                value="isLicencie"
                                {...register('selectLicencieOrAncienLicencieSki')}
                                disabled={selectedType && selectedType !== 'ski'}
                            />
                            <RadioControl
                                id={useId()}
                                label="Ancien licencié"
                                value="isAncienLicencie"
                                {...register('selectLicencieOrAncienLicencieSki')}
                                disabled={selectedType && selectedType !== 'ski'}
                            />
                        </FieldsetControl>
                        <InputControl
                            id="numeroLicenceSkiMineur"
                            name="numeroLicenceSki"
                            label={`Numéro de licence de Ski ${
                                watch('selectLicencieOrAncienLicencieSki') === 'isLicencie'
                                    ? '*'
                                    : ''
                            }`}
                            disabled={
                                !watch('selectLicencieOrAncienLicencieSki') ||
                                (selectedType && selectedType !== 'ski')
                            }
                            {...register('numeroLicenceSki')}
                            uppercase
                            state={formState.errors?.numeroLicenceSki ? 'error' : null}
                            message={formState.errors?.numeroLicenceSki?.message}
                            onChange={() => clearErrors('numeroLicenceSki')}
                        />
                        {watch('selectLicencieOrAncienLicencieSki') && (
                            <InputFiles
                                name="versoLicenceSki"
                                label="Importer la licence"
                                error={formState.errors ? formState.errors.versoLicenceSki : null}
                                required={
                                    watch('selectLicencieOrAncienLicencieSki') === 'isLicencie'
                                }
                                disabled={selectedType && selectedType !== 'ski'}
                            />
                        )}
                    </div>
                </Col>
            </Row>
        </Container>
    );
};

InformationLicencieMineur.propTypes = {
    featureFlipping: PropTypes.shape({
        feature_appel_api_fftir: PropTypes.bool.isRequired,
    }),
};

export default InformationLicencieMineur;
